import React, { useState, useRef, useEffect } from 'react';
import { Card, BtnPrimary, BtnOutline, SectionTitle, Avatar, RankBadge } from './UI';
import { callClaude, BATTLE_MODES, OPPONENTS, buildMySystem, buildOppSystem } from '../lib/claude';

function ModeCard({ mode, modeKey, selected, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 14px',
        border: `1px solid ${selected ? 'var(--purple)' : 'var(--border)'}`,
        borderRadius: 12,
        background: selected ? 'rgba(108,59,255,0.12)' : 'transparent',
        cursor: 'pointer', marginBottom: 8, transition: 'all 0.15s'
      }}
    >
      <div style={{
        width: 44, height: 44, borderRadius: 10,
        background: 'rgba(255,255,255,0.06)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22
      }}>{mode.emoji}</div>
      <div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700 }}>{mode.label}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{mode.desc}</div>
      </div>
    </div>
  );
}

function ChatBubble({ text, isRight, label, isTyping }) {
  return (
    <div style={{ marginBottom: 12, animation: 'slideUp 0.3s ease' }}>
      <div style={{
        fontSize: 11, color: 'var(--text-muted)',
        marginBottom: 3, textAlign: isRight ? 'right' : 'left'
      }}>{label}</div>
      <div style={{
        maxWidth: '85%', padding: '10px 14px',
        borderRadius: isRight ? '14px 4px 14px 14px' : '4px 14px 14px 14px',
        background: isRight ? 'var(--purple)' : 'rgba(255,255,255,0.07)',
        color: isRight ? 'white' : 'var(--text)',
        fontSize: 14, lineHeight: 1.5,
        marginLeft: isRight ? 'auto' : 0,
      }}>
        {isTyping ? (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '2px 4px' }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 7, height: 7, borderRadius: '50%',
                background: isRight ? 'rgba(255,255,255,0.6)' : 'var(--text-muted)',
                animation: `pulse 1.2s infinite ${i * 0.2}s`
              }} />
            ))}
          </div>
        ) : text}
      </div>
    </div>
  );
}

export default function ArenaScreen({ botConfig, stats, onBattleEnd }) {
  const [phase, setPhase] = useState('setup');
  const [selectedMode, setSelectedMode] = useState('banat');
  const [selectedOpp, setSelectedOpp] = useState(OPPONENTS[0]);
  const [topic, setTopic] = useState('');
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const logRef = useRef(null);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, isTyping]);

  async function startBattle() {
    const mode = BATTLE_MODES[selectedMode];
    const t = mode.topics[Math.floor(Math.random() * mode.topics.length)];
    setTopic(t);
    setMessages([]);
    setResult(null);
    setError('');
    setPhase('battle');

    const mySystem = buildMySystem(botConfig.name, botConfig.style, botConfig.instrux, selectedMode, t);
    const oppSystem = buildOppSystem(selectedOpp, selectedMode, t);

    let myMsgs = [];
    let oppMsgs = [];

    try {
      for (let round = 0; round < 2; round++) {
        setIsTyping('me');
        const myPrompt = round === 0
          ? [{ role: 'user', content: 'Start the battle! Deliver your opening argument or scene.' }]
          : [...myMsgs, { role: 'user', content: oppMsgs[oppMsgs.length - 1]?.content || 'Continue.' }];

        const myReply = await callClaude(mySystem, myPrompt);
        setIsTyping(null);
        setMessages(prev => [...prev, { text: myReply, isRight: true, label: botConfig.name }]);
        myMsgs = [...myMsgs, { role: 'assistant', content: myReply }];
        oppMsgs = [...oppMsgs, { role: 'user', content: myReply }];

        await new Promise(r => setTimeout(r, 500));
        setIsTyping('opp');
        const oppPrompt = round === 0
          ? [{ role: 'user', content: `Your opponent said: "${myReply}". Respond and counter-argue.` }]
          : [...oppMsgs.slice(0, -1), { role: 'user', content: `Your opponent said: "${myReply}". Respond now.` }];

        const oppReply = await callClaude(oppSystem, oppPrompt);
        setIsTyping(null);
        setMessages(prev => [...prev, { text: oppReply, isRight: false, label: selectedOpp.name }]);
        oppMsgs = [...oppMsgs.slice(0, -1), { role: 'user', content: myReply }, { role: 'assistant', content: oppReply }];
        myMsgs = [...myMsgs, { role: 'user', content: oppReply }];

        await new Promise(r => setTimeout(r, 400));
      }

      const roll = Math.random();
      const won = roll > 0.42;
      const draw = !won && roll > 0.65;
      const eloChange = won ? Math.floor(Math.random() * 25) + 15 : draw ? 5 : -(Math.floor(Math.random() * 10) + 3);
      setResult({ won, draw, eloChange });
      onBattleEnd({ won, draw, eloChange });

    } catch (e) {
      setIsTyping(null);
      setError(e.message || 'May error. Subukan ulit!');
    }
  }

  if (phase === 'setup') {
    return (
      <div style={{ animation: 'slideUp 0.3s ease' }}>
        <Card>
          <SectionTitle>Pick your battle mode</SectionTitle>
          {Object.entries(BATTLE_MODES).map(([key, mode]) => (
            <ModeCard key={key} modeKey={key} mode={mode} selected={selectedMode === key} onClick={() => setSelectedMode(key)} />
          ))}
        </Card>

        <Card>
          <SectionTitle>Pick your opponent</SectionTitle>
          {OPPONENTS.map(opp => (
            <div
              key={opp.id}
              onClick={() => setSelectedOpp(opp)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                borderBottom: '1px solid var(--border)',
                cursor: 'pointer', opacity: selectedOpp.id === opp.id ? 1 : 0.6,
                transition: 'opacity 0.15s'
              }}
            >
              <div style={{ fontSize: 28 }}>{opp.emoji}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{opp.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{opp.elo} ELO</div>
              </div>
              {selectedOpp.id === opp.id && (
                <div style={{ color: 'var(--purple)', fontSize: 18 }}>✓</div>
              )}
            </div>
          ))}
          <BtnPrimary onClick={startBattle}>FIGHT NA! ⚡</BtnPrimary>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ animation: 'slideUp 0.3s ease' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: 36, marginBottom: 4 }}>⚡</div>
            <div style={{ fontSize: 12, fontWeight: 500 }}>{botConfig.name}</div>
            <RankBadge elo={stats.elo} />
          </div>
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 800,
            color: 'var(--coral)', padding: '0 12px'
          }}>VS</div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: 36, marginBottom: 4 }}>{selectedOpp.emoji}</div>
            <div style={{ fontSize: 12, fontWeight: 500 }}>{selectedOpp.name}</div>
            <span style={{
              display: 'inline-block', padding: '3px 10px', borderRadius: 20,
              background: 'rgba(255,87,51,0.15)', color: 'var(--coral)',
              fontSize: 12, fontWeight: 500
            }}>{selectedOpp.elo} ELO</span>
          </div>
        </div>

        <div style={{
          textAlign: 'center', fontSize: 12, fontWeight: 700,
          color: 'var(--gold)', letterSpacing: '0.5px',
          background: 'rgba(255,215,0,0.08)', borderRadius: 8, padding: '8px 12px',
          marginBottom: 16
        }}>
          🎯 {topic}
        </div>

        <div ref={logRef} style={{ minHeight: 200, maxHeight: 340, overflowY: 'auto', paddingRight: 4 }}>
          {messages.map((m, i) => (
            <ChatBubble key={i} text={m.text} isRight={m.isRight} label={m.label} />
          ))}
          {isTyping === 'me' && <ChatBubble isRight={true} label={botConfig.name} isTyping />}
          {isTyping === 'opp' && <ChatBubble isRight={false} label={selectedOpp.name} isTyping />}
        </div>

        {error && (
          <div style={{ color: 'var(--coral)', fontSize: 13, textAlign: 'center', marginTop: 12 }}>
            ⚠️ {error}
          </div>
        )}

        {result && (
          <div style={{
            textAlign: 'center', marginTop: 16, padding: '1rem',
            borderRadius: 12, animation: 'pop 0.3s ease',
            background: result.won ? 'rgba(0,212,170,0.1)' : result.draw ? 'rgba(108,59,255,0.1)' : 'rgba(255,87,51,0.1)'
          }}>
            <div style={{
              fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 800,
              color: result.won ? 'var(--teal)' : result.draw ? 'var(--purple)' : 'var(--coral)'
            }}>
              {result.won ? 'YOU WON! Grabe ka! 🎉' : result.draw ? 'DRAW! So close, pre! 🤝' : 'You lost... but it\'s okay! 💪'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
              {result.won
                ? `+${result.eloChange} ELO · Keep going, lodi!`
                : result.draw
                  ? `+${result.eloChange} ELO · So close naman!`
                  : `${result.eloChange} ELO · You didn't lose — you're just still training, pre!`}
            </div>
            <BtnPrimary onClick={() => setPhase('setup')} style={{ marginTop: 12 }}>New Battle 🔄</BtnPrimary>
            <BtnOutline onClick={() => setPhase('setup')}>Improve your Alalay ✏️</BtnOutline>
          </div>
        )}
      </Card>
    </div>
  );
}
